import { getToken as _getStoredToken, removeToken as _removeStoredToken, setSessionExpired as _setSessionExpired } from './auth-store.js';

// serde_wasm_bindgen returns Map objects; convert to plain objects
function obj(v) {
  if (v instanceof Map) return Object.fromEntries(v);
  return v;
}

// Zero out one or more Uint8Arrays after use. Null/undefined are silently
// skipped so callers can wipe defensively without prior null checks.
//
// JS lacks Rust's `Zeroizing<T>`. WASM key material handed back over the FFI
// boundary lives in a JS `Uint8Array` until garbage-collected — which can be
// many minutes. `wipe()` is the manual equivalent: zero the buffer the moment
// the key is no longer needed so a subsequent leak (heap dump, dev-tools
// inspection, post-free reuse) sees zeros instead of usable secret bytes.
// See issue #136.
export function wipe(...buffers) {
  for (const b of buffers) {
    if (b && typeof b.fill === 'function') b.fill(0);
  }
}

// V1 format: 0x01 + nonce(24) + ciphertext (with AAD)
function encryptAndConcatV1(key, data, aad) {
  const aadBytes = typeof aad === 'string' ? new TextEncoder().encode(aad) : aad;
  const p = obj(wasm.encrypt_v1(key, data, aadBytes));
  const ct = new Uint8Array(p.ciphertext); // raw V1 AEAD ciphertext from WASM (0x01 prefix added below)
  const n = new Uint8Array(p.nonce);
  const r = new Uint8Array(1 + n.length + ct.length);
  r[0] = 0x01;
  r.set(n, 1);
  r.set(ct, 1 + n.length);
  return r;
}

// Decrypt a concatenated blob (nonce + ciphertext, with optional V1 0x01 prefix).
//
// `formatVersion` (Phase 2 of #122) is the per-row column the server ships:
//   - 1: V1 path (slice past 0x01). **No V0 fallback** — Phase 4 closed the
//        cross-format `try/catch` after confirming prod has zero V0 rows,
//        restoring AAD's defensive value as a ciphertext-substitution backstop.
//   - 0: V0 path only. No upward retry to V1.
//   - undefined / null: strict first-byte dispatch (`0x01` → V1, else → V0).
//        Kept for callers that still don't pass the column; no cross-format
//        retry, same as the column-driven branches.
function decryptConcatAuto(key, blob, aad, formatVersion = undefined) {
  const b = new Uint8Array(blob);
  const aadBytes = typeof aad === 'string' ? new TextEncoder().encode(aad) : aad;

  const tryV1 = () => {
    if (b[0] !== 0x01 || b.length <= 25) throw new Error('not V1');
    const nonce = b.slice(1, 25);
    const ciphertext = b.slice(25);
    return new Uint8Array(wasm.decrypt_auto(key, ciphertext, nonce, aadBytes));
  };
  const tryV0 = () => {
    const nonce = b.slice(0, 24);
    const ciphertext = b.slice(24);
    return new Uint8Array(wasm.decrypt(key, ciphertext, nonce));
  };

  if (formatVersion === 1) {
    return tryV1();
  }
  if (formatVersion === 0) {
    return tryV0();
  }
  // Heuristic: dispatch by leading byte; no cross-format retry.
  if (b[0] === 0x01) {
    return tryV1();
  }
  return tryV0();
}

let _userId = null;
export function setUserId(id) { _userId = id; }
export function getUserId() { return _userId; }

// Extract user_id from JWT token in sessionStorage
function syncUserIdFromToken() {
  try {
    const token = _getStoredToken();
    if (!token) return;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.sub) _userId = payload.sub;
  } catch (_) {}
}

let wasm = null;
let masterKey = null;
let encKey = null;
let wrapKey = null;
let privateKey = null;
let publicKey = null;

export async function initWasm() {
  if (wasm) return;
  const mod = await import('./pkg/vault_wasm.js');
  await mod.default();
  wasm = mod;
}

export function getWasm() { return wasm; }

export function hasKeys() {
  return encKey !== null;
}

export function getEncKey() {
  if (!encKey) throw new Error('Keys not derived yet');
  return new Uint8Array(encKey);
}

let authKey = null;

// Track whether keys were derived with legacy Argon2 params (needs re-wrap)
let _needsParamUpgrade = false;

// Track whether registration-era unbound AAD was used to unwrap (needs re-wrap
// with user-bound AAD: master:{user_id} / privkey:{user_id}).
let _needsAadUpgrade = false;

export function needsParamUpgrade() { return _needsParamUpgrade; }
export function needsAadUpgrade() { return _needsAadUpgrade; }

// `mkFormatVersion` / `pkFormatVersion` are the per-row Phase 2 columns
// (#122) for `encryptedMasterKey` / `encryptedPrivateKey`. Optional —
// when undefined, falls through to the pre-Phase-2 first-byte heuristic
// in `decryptConcatAuto`.
export function deriveAndStoreKeys(
  password,
  saltArr,
  encryptedPrivateKey,
  pubKeyArr,
  encryptedMasterKey,
  mkFormatVersion = undefined,
  pkFormatVersion = undefined,
) {
  syncUserIdFromToken();
  _needsParamUpgrade = false;
  _needsAadUpgrade = false;
  // Zeroize any previous session keys before overwriting so the old bytes don't
  // linger on the JS heap when re-deriving (e.g. login-after-lock without an
  // explicit clearKeys).
  wipe(masterKey, encKey, wrapKey, privateKey, authKey);
  const salt = new Uint8Array(saltArr);
  const passwordKey = new Uint8Array(wasm.derive_key(password, salt));
  authKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-auth'));
  // Track whether passwordKey was adopted as masterKey (legacy path) — in
  // that case the lock/clear path owns its lifetime, so the outer finally
  // skips it.
  let passwordKeyAdoptedAsMaster = false;
  let keyWrappingKey;

  try {
    if (encryptedMasterKey) {
      // New path: unwrap master_key with password-derived key_wrapping_key
      keyWrappingKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-enc'));
      const emk = new Uint8Array(encryptedMasterKey);
      let mk;
      try {
        mk = decryptConcatAuto(keyWrappingKey, emk, _userId ? 'master:' + _userId : '', mkFormatVersion);
      } catch (_1) {
        try {
          // Registration-time keys use empty AAD (user_id not yet known).
          // Pass the same format_version: a V1 row with empty AAD still
          // needs the V1 path; a V0 row would have already succeeded above
          // (V0 ignores AAD entirely), so this branch is V1-empty-AAD or a
          // genuine failure.
          mk = decryptConcatAuto(keyWrappingKey, emk, '', mkFormatVersion);
          // Empty-AAD succeeded — flag for re-wrap with user-bound AAD
          if (_userId) _needsAadUpgrade = true;
        } catch (_2) {
          // Decryption failed with new params — try legacy Argon2 params (19 MiB, 2 iter)
          const legacyPasswordKey = new Uint8Array(wasm.derive_key_legacy(password, salt));
          const legacyKwk = new Uint8Array(wasm.derive_subkey(legacyPasswordKey, 'vault-enc'));
          try {
            mk = decryptConcatAuto(legacyKwk, emk, _userId ? 'master:' + _userId : '', mkFormatVersion);
          } finally {
            wipe(legacyPasswordKey, legacyKwk);
          }
          // Legacy succeeded — re-derive auth key with new params for future server auth,
          // and flag for re-wrapping the master key
          _needsParamUpgrade = true;
        }
      }
      masterKey = mk;
      encKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-enc'));
      wrapKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-wrap'));
    } else {
      // Legacy path: password directly derives keys (no master key yet).
      // passwordKey is adopted as masterKey; the outer finally skips wiping it.
      masterKey = passwordKey;
      passwordKeyAdoptedAsMaster = true;
      encKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-enc'));
      wrapKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-wrap'));
    }

    if (encryptedPrivateKey) {
      const epk = new Uint8Array(encryptedPrivateKey);
      try {
        privateKey = decryptConcatAuto(encKey, epk, _userId ? 'privkey:' + _userId : '', pkFormatVersion);
      } catch (_) {
        // Registration-time keys use 'privkey' AAD (no user_id suffix)
        privateKey = decryptConcatAuto(encKey, epk, 'privkey', pkFormatVersion);
        if (_userId) _needsAadUpgrade = true;
      }
    }
    if (pubKeyArr) publicKey = new Uint8Array(pubKeyArr);
  } finally {
    wipe(keyWrappingKey);
    // In the legacy path, masterKey === passwordKey and clearKeys() will
    // wipe it on lock; wiping here would zero the live master key.
    if (!passwordKeyAdoptedAsMaster) wipe(passwordKey);
  }
}

export function getAuthKeyHex() {
  if (!authKey) throw new Error('Keys not derived yet');
  return Array.from(authKey).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function generateRegistrationKeys(password) {
  wipe(masterKey, encKey, wrapKey, privateKey, authKey);
  const clientSalt = new Uint8Array(16);
  crypto.getRandomValues(clientSalt);
  const passwordKey = new Uint8Array(wasm.derive_key(password, clientSalt));
  let keyWrappingKey;
  try {
    authKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-auth'));

    const mk = new Uint8Array(wasm.generate_random_key());
    masterKey = mk;
    encKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-enc'));
    wrapKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-wrap'));

    // Wrap master_key (V1 with empty AAD — user_id not yet known at registration).
    keyWrappingKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-enc'));
    const encryptedMasterKey = encryptAndConcatV1(keyWrappingKey, mk, '');

    const kp = obj(wasm.generate_keypair());
    privateKey = new Uint8Array(kp.private_key);
    publicKey = new Uint8Array(kp.public_key);
    const encryptedPrivateKey = encryptAndConcatV1(encKey, privateKey, 'privkey');

    return {
      client_salt: Array.from(clientSalt),
      public_key: Array.from(publicKey),
      encrypted_private_key: Array.from(encryptedPrivateKey),
      auth_key: Array.from(authKey),
      encrypted_master_key: Array.from(encryptedMasterKey),
    };
  } finally {
    wipe(passwordKey, keyWrappingKey);
  }
}

export function generateRegistrationKeysPasswordless() {
  wipe(masterKey, encKey, wrapKey, privateKey, authKey);
  const mk = new Uint8Array(wasm.generate_random_key());
  masterKey = mk;
  encKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-enc'));
  wrapKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-wrap'));
  const kp = obj(wasm.generate_keypair());
  privateKey = new Uint8Array(kp.private_key);
  publicKey = new Uint8Array(kp.public_key);
  const encryptedPrivateKey = encryptAndConcatV1(encKey, privateKey, 'privkey');
  const clientSalt = new Uint8Array(16);
  crypto.getRandomValues(clientSalt);
  return {
    client_salt: Array.from(clientSalt),
    public_key: Array.from(publicKey),
    encrypted_private_key: Array.from(encryptedPrivateKey),
  };
}

function uint8ToBase64(bytes) {
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
}

export function encryptItem(plaintext) {
  const itemKey = new Uint8Array(wasm.generate_random_key());
  try {
    const textBytes = new TextEncoder().encode(plaintext);
    const padded = padPlaintext(textBytes);
    const aad = _userId ? 'item:' + _userId : '';
    const combined = encryptAndConcatV1(itemKey, padded, aad);

    // Wrap item key with encKey (V1; AAD empty when no user_id yet).
    const wrapAad = _userId ? 'wrap:' + _userId : '';
    const wrappedPayload = obj(wasm.encrypt_v1(encKey, itemKey, new TextEncoder().encode(wrapAad)));
    const wrappedKey = new Uint8Array(wrappedPayload.ciphertext);
    const wrapNonce = new Uint8Array(wrappedPayload.nonce);

    return {
      encrypted_blob: uint8ToBase64(combined),
      wrapped_key: Array.from(wrappedKey),
      nonce: Array.from(wrapNonce),
    };
  } finally {
    wipe(itemKey);
  }
}

function decryptBlob(itemKey, encryptedBlob, aad = '', formatVersion = undefined) {
  // blob = base64(V1: 0x01+nonce+ct | V0: nonce+ct)
  const blobBytes = Uint8Array.from(atob(encryptedBlob), c => c.charCodeAt(0));
  const plainBytes = decryptBlobRaw(itemKey, blobBytes, aad, formatVersion);
  try {
    return new TextDecoder().decode(unpadPlaintext(plainBytes));
  } catch (_) {
    return new TextDecoder().decode(plainBytes);
  }
}

// See `decryptConcatAuto` for the meaning of `formatVersion` (Phase 2 of #122).
function decryptBlobRaw(itemKey, blobBytes, aad = '', formatVersion = undefined) {
  return decryptConcatAuto(itemKey, blobBytes, aad, formatVersion);
}

// Unwrap item key with auto version detection.
//
// Dead drop items: wrapped_key is 105 bytes (eph_pubkey(32) + nonce(24) + ciphertext(49))
// Normal items: wrapped_key is typically 48-49 bytes, decrypted with encKey.
//
// `formatVersion` is `items.wrapped_key_format_version` (Phase 2 of #122) —
// the V0/V1 marker for the symmetric wrap branch only. Dead-drop X25519
// wraps are V1-only and ignore the column. When `formatVersion` is
// undefined the symmetric branch falls through to `wasm.decrypt_auto`,
// which is V1-with-V0-fallback today (matches pre-Phase-2 behaviour).
function unwrapItemKey(wrappedKey, nonce, formatVersion = undefined) {
  const wk = new Uint8Array(wrappedKey);
  // Detect X25519-wrapped keys from dead drop inboxes (105 bytes)
  if (wk.length >= 100 && privateKey) {
    const ep = wk.slice(0, 32);
    const wn = wk.slice(32, 56);
    const ct = wk.slice(56);
    return new Uint8Array(wasm.unwrap_key_v1(privateKey, ep, ct, wn, publicKey));
  }
  // Normal master-key-wrapped (V1 with AAD).
  const wn = new Uint8Array(nonce);
  const wrapAad = _userId ? 'wrap:' + _userId : '';
  const aadBytes = new TextEncoder().encode(wrapAad);

  if (formatVersion === 0) {
    // Column says V0 — `wk` is raw V0 ciphertext (no 0x01 prefix), and the
    // wrap column has no length-prefix-byte to slice past. Use the raw
    // V0 decrypt path; **no upward retry to V1**, matching the Rust-side
    // selector's protection.
    return new Uint8Array(wasm.decrypt(encKey, wk, wn));
  }
  if (formatVersion === 1) {
    // Column says V1 — `decrypt_auto` already retains the V1→V0 `or_else`
    // for the ~1/256 backfill misclassifications.
    return new Uint8Array(wasm.decrypt_auto(encKey, wk, wn, aadBytes));
  }
  // undefined — legacy heuristic-equivalent. `decrypt_auto` is V1 with V0
  // fallback inside the wasm shim, so this preserves pre-Phase-2 behaviour
  // for callers not yet threaded with the column.
  return new Uint8Array(wasm.decrypt_auto(encKey, wk, wn, aadBytes));
}

// `wrapFormatVersion` and `blobFormatVersion` are the per-row Phase 2 of
// #122 columns from the API: `items.wrapped_key_format_version` and
// `items.encrypted_blob_format_version`. Optional — when undefined the
// helpers fall through to the pre-Phase-2 first-byte heuristic.
export function getRawItemKey(wrappedKey, nonce, wrapFormatVersion = undefined) {
  return unwrapItemKey(wrappedKey, nonce, wrapFormatVersion);
}

export function decryptItem(encryptedBlob, wrappedKey, nonce, wrapFormatVersion = undefined, blobFormatVersion = undefined) {
  const itemKey = unwrapItemKey(wrappedKey, nonce, wrapFormatVersion);
  const aad = _userId ? 'item:' + _userId : '';
  try {
    return decryptBlob(itemKey, encryptedBlob, aad, blobFormatVersion);
  } finally {
    wipe(itemKey);
  }
}

export function decryptItemRaw(encryptedBlob, wrappedKey, nonce, wrapFormatVersion = undefined, blobFormatVersion = undefined) {
  const itemKey = unwrapItemKey(wrappedKey, nonce, wrapFormatVersion);
  const blobBytes = Uint8Array.from(atob(encryptedBlob), c => c.charCodeAt(0));
  const aad = _userId ? 'item:' + _userId : '';
  try {
    const plainBytes = decryptBlobRaw(itemKey, blobBytes, aad, blobFormatVersion);
    try {
      return unpadPlaintext(plainBytes);
    } catch (_) {
      return plainBytes;
    }
  } finally {
    wipe(itemKey);
  }
}

export function wrapForGrant(wrappedKey, nonce, recipientPubkey) {
  const itemKey = unwrapItemKey(wrappedKey, nonce);
  try {
    const rpk = new Uint8Array(recipientPubkey);
    const result = obj(wasm.wrap_key_for_grant(itemKey, rpk));
    return {
      grant_wrapped_key: Array.from(new Uint8Array(result.grant_wrapped_key)),
      ephemeral_pubkey: Array.from(new Uint8Array(result.ephemeral_pubkey)),
    };
  } finally {
    wipe(itemKey);
  }
}

export function decryptGrant(grantWrappedKey, ephPub, blob, grantorId) {
  const gwk = new Uint8Array(grantWrappedKey);
  const ep = new Uint8Array(ephPub);
  const itemKey = new Uint8Array(wasm.unwrap_grant_key(privateKey, ep, gwk, publicKey));
  // AAD must match the grantor's user_id (who encrypted the item)
  const ownerId = grantorId || _userId;
  const aad = ownerId ? 'item:' + ownerId : '';
  try {
    return decryptBlob(itemKey, blob, aad);
  } finally {
    wipe(itemKey);
  }
}

export function encryptFileBytes(uint8Array) {
  const itemKey = new Uint8Array(wasm.generate_random_key());
  try {
    const padded = padPlaintext(uint8Array);
    const aad = _userId ? 'item:' + _userId : '';
    const combined = encryptAndConcatV1(itemKey, padded, aad);

    // V1 wrap; AAD empty when no user_id yet.
    const wrapAad = _userId ? 'wrap:' + _userId : '';
    const wrappedPayload = obj(wasm.encrypt_v1(encKey, itemKey, new TextEncoder().encode(wrapAad)));
    const wrappedKey = new Uint8Array(wrappedPayload.ciphertext);
    const wrapNonce = new Uint8Array(wrappedPayload.nonce);

    return {
      encrypted_blob: combined,
      wrapped_key: Array.from(wrappedKey),
      nonce: Array.from(wrapNonce),
    };
  } finally {
    wipe(itemKey);
  }
}

export function decryptFileBytes(uint8Array, wrappedKey, nonce) {
  const itemKey = unwrapItemKey(wrappedKey, nonce);
  const aad = _userId ? 'item:' + _userId : '';
  try {
    const plainBytes = decryptBlobRaw(itemKey, uint8Array, aad);
    try {
      return unpadPlaintext(plainBytes);
    } catch (_) {
      return plainBytes;
    }
  } finally {
    wipe(itemKey);
  }
}

// --- Base64url helpers ---
export function base64urlEncode(uint8Array) {
  const s = btoa(String.fromCharCode(...uint8Array));
  return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64urlDecode(str) {
  let s = str.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

// --- Re-share helpers (grantee wraps for new recipient) ---

export function rewrapGrantForRecipient(grantWrappedKey, grantEphPub, recipientPubkey) {
  const gwk = new Uint8Array(grantWrappedKey);
  const ep = new Uint8Array(grantEphPub);
  const itemKey = new Uint8Array(wasm.unwrap_grant_key(privateKey, ep, gwk, publicKey));
  try {
    const rpk = new Uint8Array(recipientPubkey);
    const result = obj(wasm.wrap_key_for_grant(itemKey, rpk));
    return {
      grant_wrapped_key: Array.from(new Uint8Array(result.grant_wrapped_key)),
      ephemeral_pubkey: Array.from(new Uint8Array(result.ephemeral_pubkey)),
    };
  } finally {
    wipe(itemKey);
  }
}

/** Unwrap a grant's item key from X25519 format. Returns raw 32-byte Uint8Array. */
export function unwrapReceivedGrantKey(grantWrappedKey, grantEphPub) {
  const gwk = new Uint8Array(grantWrappedKey);
  const ep = new Uint8Array(grantEphPub);
  return new Uint8Array(wasm.unwrap_grant_key(privateKey, ep, gwk, publicKey));
}

export function unwrapGrantFileKey(grantFileWrappedKey) {
  // X25519 format: 32 eph + 24 nonce + ciphertext
  const fwk = new Uint8Array(grantFileWrappedKey);
  const ep = fwk.slice(0, 32);
  const n = fwk.slice(32, 56);
  const ct = fwk.slice(56);
  return new Uint8Array(wasm.unwrap_key_v1(privateKey, ep, ct, n, publicKey));
}

// --- Link-secret grant helpers ---

export function wrapForLinkSecret(wrappedKey, nonce, grantId) {
  const itemKey = unwrapItemKey(wrappedKey, nonce);
  try {
    const linkSecret = new Uint8Array(32);
    crypto.getRandomValues(linkSecret);
    // V1; AAD is 'linksecret:<grantId>' when grant_id known, otherwise empty.
    const aad = grantId ? 'linksecret:' + grantId : '';
    const payload = obj(wasm.encrypt_v1(linkSecret, itemKey, new TextEncoder().encode(aad)));
    return {
      wrapped_key: Array.from(new Uint8Array(payload.ciphertext)),
      nonce: Array.from(new Uint8Array(payload.nonce)),
      link_secret: linkSecret,
    };
  } finally {
    wipe(itemKey);
  }
}

export function decryptWithLinkSecret(wrappedKey, nonce, linkSecret, encryptedBlob, grantId, grantorId) {
  const wk = new Uint8Array(wrappedKey);
  const wn = new Uint8Array(nonce);
  const ls = new Uint8Array(linkSecret);
  const aad = grantId ? 'linksecret:' + grantId : '';
  const aadBytes = new TextEncoder().encode(aad);
  const itemKey = new Uint8Array(wasm.decrypt_auto(ls, wk, wn, aadBytes));
  // AAD must match the grantor's user_id (who encrypted the item)
  const ownerId = grantorId || _userId;
  const blobAad = ownerId ? 'item:' + ownerId : '';
  try {
    return decryptBlob(itemKey, encryptedBlob, blobAad);
  } finally {
    wipe(itemKey, ls);
  }
}

export function rewrapWithOwnKeys(wrappedKey, nonce, linkSecret, grantId) {
  const wk = new Uint8Array(wrappedKey);
  const wn = new Uint8Array(nonce);
  const ls = new Uint8Array(linkSecret);
  const aad = grantId ? 'linksecret:' + grantId : '';
  const aadBytes = new TextEncoder().encode(aad);
  const itemKey = new Uint8Array(wasm.decrypt_auto(ls, wk, wn, aadBytes));
  try {
    const result = obj(wasm.wrap_key_for_recipient_v1(itemKey, publicKey));
    return {
      wrapped_key: Array.from(new Uint8Array(result.wrapped_key)),
      ephemeral_pubkey: Array.from(new Uint8Array(result.ephemeral_pubkey)),
      nonce: Array.from(new Uint8Array(result.nonce)),
    };
  } finally {
    wipe(itemKey, ls);
  }
}

export function getFileKeyFromEnvelope(envelopeWrappedKey, envelopeNonce) {
  return unwrapItemKey(envelopeWrappedKey, envelopeNonce);
}

// Rewrap a per-item key from link-secret to own X25519 keys
export function rewrapItemKeyWithOwnKeys(wrappedKey, nonce, linkSecret) {
  const rawKey = unwrapGrantItemKeyWithLinkSecret(wrappedKey, nonce, linkSecret);
  try {
    const result = obj(wasm.wrap_key_for_recipient_v1(rawKey, publicKey));
    return {
      wrapped_key: Array.from(new Uint8Array(result.wrapped_key)),
      ephemeral_pubkey: Array.from(new Uint8Array(result.ephemeral_pubkey)),
      nonce: Array.from(new Uint8Array(result.nonce)),
    };
  } finally {
    wipe(rawKey);
  }
}

// Unwrap a per-item key from a group grant's wrapped_item_keys entry (X25519 flow)
export function unwrapGrantItemKey(wrappedKey, nonce, ephemeralPubkey) {
  const wk = new Uint8Array(wrappedKey);
  const wn = new Uint8Array(nonce);
  const ep = new Uint8Array(ephemeralPubkey);
  return new Uint8Array(wasm.unwrap_key_v1(privateKey, ep, wk, wn, publicKey));
}

// Unwrap a per-item key from a group grant's wrapped_item_keys entry (link-secret flow)
export function unwrapGrantItemKeyWithLinkSecret(wrappedKey, nonce, linkSecret) {
  const wk = new Uint8Array(wrappedKey);
  const wn = new Uint8Array(nonce);
  const ls = new Uint8Array(linkSecret);
  const aadBytes = new Uint8Array(0);
  return new Uint8Array(wasm.decrypt_auto(ls, wk, wn, aadBytes));
}

// Decrypt an encrypted_blob using a raw item key (for group grant item display)
export function decryptBlobWithKey(rawItemKey, encryptedBlob, ownerId) {
  const key = new Uint8Array(rawItemKey);
  const aad = ownerId ? 'item:' + ownerId : '';
  return decryptBlob(key, encryptedBlob, aad);
}

export function wrapRawKeyForGrant(rawKey, recipientPubkey) {
  const rpk = new Uint8Array(recipientPubkey);
  const result = obj(wasm.wrap_key_for_recipient_v1(new Uint8Array(rawKey), rpk));
  return {
    wrapped_key: Array.from(new Uint8Array(result.wrapped_key)),
    ephemeral_pubkey: Array.from(new Uint8Array(result.ephemeral_pubkey)),
    nonce: Array.from(new Uint8Array(result.nonce)),
  };
}

export function wrapRawKeyForLinkSecret(rawKey, linkSecret, grantId) {
  const ls = new Uint8Array(linkSecret);
  const aad = grantId ? 'linksecret:' + grantId : '';
  const payload = obj(wasm.encrypt_v1(ls, new Uint8Array(rawKey), new TextEncoder().encode(aad)));
  return {
    wrapped_key: Array.from(new Uint8Array(payload.ciphertext)),
    nonce: Array.from(new Uint8Array(payload.nonce)),
  };
}

export function unwrapFileKey(fileWrappedKey, fileNonce) {
  return unwrapItemKey(fileWrappedKey, fileNonce);
}

// file_wrapped_key format for X25519: ephemeral_pubkey(32) + nonce(24) + ciphertext
export function decryptGrantFileBlob(fileWrappedKeyBytes, blobUint8Array, grantorId) {
  const fwk = new Uint8Array(fileWrappedKeyBytes);
  const ep = fwk.slice(0, 32);
  const nonce = fwk.slice(32, 56);
  const ct = fwk.slice(56);
  const fileKey = new Uint8Array(wasm.unwrap_key_v1(privateKey, ep, ct, nonce, publicKey));
  const ownerId = grantorId || _userId;
  const aad = ownerId ? 'item:' + ownerId : '';
  try {
    const plainBytes = decryptBlobRaw(fileKey, blobUint8Array, aad);
    try {
      return unpadPlaintext(plainBytes);
    } catch (_) {
      return plainBytes;
    }
  } finally {
    wipe(fileKey);
  }
}

// file_wrapped_key format for link-secret: nonce(24) + ciphertext
export function decryptFileBlobWithLinkSecret(fileWrappedKeyBytes, linkSecret, blobUint8Array, grantorId) {
  const fwk = new Uint8Array(fileWrappedKeyBytes);
  const nonce = fwk.slice(0, 24);
  const ciphertext = fwk.slice(24);
  const ls = new Uint8Array(linkSecret);
  const fileKey = new Uint8Array(wasm.decrypt_auto(ls, ciphertext, nonce, new Uint8Array(0)));
  const ownerId = grantorId || _userId;
  const aad = ownerId ? 'item:' + ownerId : '';
  try {
    const plainBytes = decryptBlobRaw(fileKey, blobUint8Array, aad);
    try {
      return unpadPlaintext(plainBytes);
    } catch (_) {
      return plainBytes;
    }
  } finally {
    wipe(fileKey, ls);
  }
}

// Rewrap a link-secret file_wrapped_key with own X25519 public key
// Input format (link-secret): nonce(24) + ciphertext
// Output format (X25519): ephemeral_pubkey(32) + nonce(24) + ciphertext
export function rewrapFileKeyWithOwnKeys(fileWrappedKeyBytes, linkSecret) {
  const fwk = new Uint8Array(fileWrappedKeyBytes);
  const nonce = fwk.slice(0, 24);
  const ciphertext = fwk.slice(24);
  const ls = new Uint8Array(linkSecret);
  let fileKey;
  try {
    if (ciphertext[0] === 0x01) {
      fileKey = new Uint8Array(wasm.decrypt_auto(ls, ciphertext, nonce, new Uint8Array(0)));
    } else {
      fileKey = new Uint8Array(wasm.decrypt(ls, ciphertext, nonce));
    }
    const result = obj(wasm.wrap_key_for_recipient_v1(fileKey, publicKey));
    const ep = new Uint8Array(result.ephemeral_pubkey);
    const wn = new Uint8Array(result.nonce);
    const wk = new Uint8Array(result.wrapped_key);
    const combined = new Uint8Array(ep.length + wn.length + wk.length);
    combined.set(ep, 0);
    combined.set(wn, ep.length);
    combined.set(wk, ep.length + wn.length);
    return Array.from(combined);
  } finally {
    wipe(fileKey, ls);
  }
}

export function getMasterKey() {
  if (!masterKey) throw new Error('Vault is locked');
  return new Uint8Array(masterKey);
}

export function wrapMasterKeyWithPrf(prfOutput) {
  const passkeyEncKey = new Uint8Array(wasm.derive_subkey(new Uint8Array(prfOutput), 'vault-passkey-v1'));
  const aad = _userId ? 'passkey:' + _userId : '';
  try {
    return Array.from(encryptAndConcatV1(passkeyEncKey, masterKey, aad));
  } finally {
    wipe(passkeyEncKey);
  }
}

// `pkFormatVersion` is `users.encrypted_private_key_format_version` from
// the API response (Phase 2 of #122). The PRF-wrapped master key has no
// per-row column yet (passkeys table is out of scope for Phase 2), so
// the master decrypt still goes through the legacy heuristic.
export function unwrapAndStoreKeysFromPrf(prfOutput, wrappedMasterKey, encryptedPrivateKey, pubKeyArr, pkFormatVersion = undefined) {
  syncUserIdFromToken();
  wipe(masterKey, encKey, wrapKey, privateKey, authKey);
  const passkeyEncKey = new Uint8Array(wasm.derive_subkey(new Uint8Array(prfOutput), 'vault-passkey-v1'));
  const wmk = new Uint8Array(wrappedMasterKey);
  const passkeyAad = _userId ? 'passkey:' + _userId : '';
  const mk = decryptConcatAuto(passkeyEncKey, wmk, passkeyAad);
  wipe(passkeyEncKey);
  masterKey = mk;
  encKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-enc'));
  wrapKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-wrap'));
  const epk = new Uint8Array(encryptedPrivateKey);
  try {
    privateKey = decryptConcatAuto(encKey, epk, _userId ? 'privkey:' + _userId : '', pkFormatVersion);
  } catch (_) {
    privateKey = decryptConcatAuto(encKey, epk, 'privkey', pkFormatVersion);
  }
  if (pubKeyArr) publicKey = new Uint8Array(pubKeyArr);
}

// --- Will crypto helpers ---

export function wrapItemKeysForWill(items, willKey) {
  const wk = new Uint8Array(willKey);
  const aad = _userId ? 'will:' + _userId : '';
  return items.map(item => {
    const itemKey = unwrapItemKey(item.wrapped_key, item.nonce, item.wrapped_key_format_version);
    try {
      const wrapped = encryptAndConcatV1(wk, itemKey, aad);
      return {
        item_id: item.id,
        wrapped_key: Array.from(wrapped),
      };
    } finally {
      wipe(itemKey);
    }
  });
}

export function encryptWillKeyForRecipient(willKey, recipientPubkey) {
  const wk = new Uint8Array(willKey);
  const rpk = new Uint8Array(recipientPubkey);
  const result = obj(wasm.wrap_key_for_recipient_v1(wk, rpk));
  const nonce = new Uint8Array(result.nonce);
  const wrapped = new Uint8Array(result.wrapped_key);
  const combined = new Uint8Array(nonce.length + wrapped.length);
  combined.set(nonce);
  combined.set(wrapped, nonce.length);
  return {
    encrypted_will_key: Array.from(combined),
    ephemeral_pubkey: Array.from(new Uint8Array(result.ephemeral_pubkey)),
  };
}

export function encryptWillKeyWithLinkSecret(willKey) {
  const wk = new Uint8Array(willKey);
  const linkSecret = new Uint8Array(32);
  crypto.getRandomValues(linkSecret);
  const aad = _userId ? 'will:' + _userId : '';
  const encrypted = encryptAndConcatV1(linkSecret, wk, aad);
  return {
    encrypted_will_key: Array.from(encrypted),
    ephemeral_pubkey: [],
    link_secret: linkSecret,
  };
}

export function decryptWillKeyWithPrivateKey(encryptedWillKey, ephemeralPubkey) {
  const ewk = new Uint8Array(encryptedWillKey);
  const ep = new Uint8Array(ephemeralPubkey);
  const nonce = ewk.slice(0, 24);
  const ciphertext = ewk.slice(24);
  if (ciphertext[0] === 0x01) {
    return new Uint8Array(wasm.unwrap_key_v1(privateKey, ep, ciphertext, nonce, publicKey));
  }
  return new Uint8Array(wasm.unwrap_key(privateKey, ep, ciphertext, nonce));
}

export function decryptWillKeyWithLinkSecret(encryptedWillKey, linkSecret) {
  const ewk = new Uint8Array(encryptedWillKey);
  const ls = new Uint8Array(linkSecret);
  // Handle both V1 (0x01 prefix) and V0 (nonce||ciphertext) formats
  return decryptConcatAuto(ls, ewk, _userId ? 'will:' + _userId : '');
}

export function decryptWillItem(willKey, wrappedEntry, encryptedBlob) {
  const wk = new Uint8Array(willKey);
  const combined = new Uint8Array(wrappedEntry.wrapped_key);
  const willAad = _userId ? 'will:' + _userId : '';
  const itemKey = decryptConcatAuto(wk, combined, willAad);
  const aad = _userId ? 'item:' + _userId : '';
  try {
    return decryptBlob(itemKey, encryptedBlob, aad);
  } finally {
    wipe(itemKey);
  }
}

export function decryptWillFileBlob(willKey, wrappedEntry, blobUint8Array) {
  const wk = new Uint8Array(willKey);
  const combined = new Uint8Array(wrappedEntry.wrapped_key);
  const willAad = _userId ? 'will:' + _userId : '';
  const itemKey = decryptConcatAuto(wk, combined, willAad);
  const aad = _userId ? 'item:' + _userId : '';
  try {
    const plainBytes = decryptBlobRaw(itemKey, blobUint8Array, aad);
    try {
      return unpadPlaintext(plainBytes);
    } catch (_) {
      return plainBytes;
    }
  } finally {
    wipe(itemKey);
  }
}

export function wrapKeyWithEncKey(rawKey) {
  const wrapAad = _userId ? 'wrap:' + _userId : '';
  const payload = obj(wasm.encrypt_v1(encKey, rawKey, new TextEncoder().encode(wrapAad)));
  return {
    wrapped_key: Array.from(new Uint8Array(payload.ciphertext)),
    nonce: Array.from(new Uint8Array(payload.nonce)),
  };
}

export function encryptFileBytesAnonymous(uint8Array, dropId) {
  const itemKey = new Uint8Array(wasm.generate_random_key());
  const padded = padPlaintext(uint8Array);
  // V1; AAD is 'drop:<dropId>' when known, otherwise empty.
  const aad = dropId ? 'drop:' + dropId : '';
  const combined = encryptAndConcatV1(itemKey, padded, aad);
  // V1 framing is 0x01 || nonce(24) || ciphertext. The nonce is self-contained
  // in the blob, but the /drops endpoint still requires a non-empty nonce field
  // (drops.nonce is NOT NULL), so surface the real nonce for the caller to send.
  const nonce = Array.from(combined.slice(1, 25));
  return { encrypted_blob: combined, key: itemKey, nonce };
}

export function decryptFileBytesWithKey(uint8Array, rawKey, dropId) {
  const aad = dropId ? 'drop:' + dropId : '';
  const plainBytes = decryptBlobRaw(rawKey, uint8Array, aad);
  try {
    return unpadPlaintext(plainBytes);
  } catch (_) {
    return plainBytes;
  }
}

export function migrateToMasterKey(password, saltArr) {
  // oldEncKey is returned to the caller (login/unlock flow) so it can re-wrap
  // existing items under the new key. The caller is responsible for wiping it
  // — see login.js / unlock.js.
  const oldEncKey = new Uint8Array(encKey);
  const mk = new Uint8Array(wasm.generate_random_key());
  const newEncKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-enc'));

  const salt = new Uint8Array(saltArr);
  const passwordKey = new Uint8Array(wasm.derive_key(password, salt));
  const keyWrappingKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-enc'));
  try {
    const masterAad = _userId ? 'master:' + _userId : '';
    const encryptedMasterKey = encryptAndConcatV1(keyWrappingKey, mk, masterAad);

    const privkeyAad = _userId ? 'privkey:' + _userId : '';
    const encryptedPrivateKey = encryptAndConcatV1(newEncKey, privateKey, privkeyAad);

    return {
      masterKey: mk,
      oldEncKey,
      newEncKey,
      encryptedMasterKey: Array.from(encryptedMasterKey),
      encryptedPrivateKey: Array.from(encryptedPrivateKey),
    };
  } finally {
    wipe(passwordKey, keyWrappingKey);
  }
}

export function rewrapItemKeys(items, oldEncKey, newEncKey) {
  // Legacy master-key migration: only triggered when me.encrypted_master_key
  // is null, which means the account predates the masterkey/V1 era. All
  // existing wraps are V0 (raw AEAD, no version prefix), so use the V0
  // decrypt directly — `wasm.decrypt_auto` would mis-route the ~1/256 V0
  // wraps whose ciphertext happens to start with 0x01 onto the V1 path.
  const wrapAad = _userId ? 'wrap:' + _userId : '';
  const wrapAadBytes = new TextEncoder().encode(wrapAad);
  return items.map(item => {
    const wk = new Uint8Array(item.wrapped_key);
    const wn = new Uint8Array(item.nonce);
    const itemKey = new Uint8Array(wasm.decrypt(oldEncKey, wk, wn));
    try {
      const payload = obj(wasm.encrypt_v1(newEncKey, itemKey, wrapAadBytes));
      return {
        item_id: item.id,
        wrapped_key: Array.from(new Uint8Array(payload.ciphertext)),
        nonce: Array.from(new Uint8Array(payload.nonce)),
      };
    } finally {
      wipe(itemKey);
    }
  });
}

export function applyMigratedKeys(mk, newEncKey, newWrapKey) {
  // The legacy path adopted passwordKey as masterKey, so the previous
  // masterKey may be aliased to encKey/wrapKey ancestors. wipe() is
  // idempotent on already-zeroed buffers, so wipe both anyway.
  wipe(masterKey, encKey, wrapKey);
  masterKey = mk;
  encKey = newEncKey;
  wrapKey = newWrapKey || new Uint8Array(wasm.derive_subkey(mk, 'vault-wrap'));
}

// Re-wrap master key with new Argon2 params (and update auth_key_hash).
// Called when legacy params were used to decrypt but we want to upgrade.
export function upgradeArgonParams(password, saltArr) {
  const salt = new Uint8Array(saltArr);
  const newPasswordKey = new Uint8Array(wasm.derive_key(password, salt));
  const newAuthKey = new Uint8Array(wasm.derive_subkey(newPasswordKey, 'vault-auth'));
  const newKeyWrappingKey = new Uint8Array(wasm.derive_subkey(newPasswordKey, 'vault-enc'));

  try {
    const masterAad = _userId ? 'master:' + _userId : '';
    const encryptedMasterKey = encryptAndConcatV1(newKeyWrappingKey, masterKey, masterAad);
    const privkeyAad = _userId ? 'privkey:' + _userId : '';
    const encryptedPrivateKey = encryptAndConcatV1(encKey, privateKey, privkeyAad);

    // Update in-memory auth key to match new params
    wipe(authKey);
    authKey = newAuthKey;
    _needsParamUpgrade = false;
    _needsAadUpgrade = false;

    return {
      auth_key: Array.from(newAuthKey).map(b => b.toString(16).padStart(2, '0')).join(''),
      encrypted_master_key: Array.from(encryptedMasterKey),
      encrypted_private_key: Array.from(encryptedPrivateKey),
    };
  } finally {
    wipe(newPasswordKey, newKeyWrappingKey);
  }
}

export function changePassword(currentPassword, currentSaltArr, newPassword) {
  const currentSalt = new Uint8Array(currentSaltArr);
  const currentPasswordKey = new Uint8Array(wasm.derive_key(currentPassword, currentSalt));
  const currentAuthKey = new Uint8Array(wasm.derive_subkey(currentPasswordKey, 'vault-auth'));

  const newSalt = new Uint8Array(16);
  crypto.getRandomValues(newSalt);
  const newPasswordKey = new Uint8Array(wasm.derive_key(newPassword, newSalt));
  const newAuthKey = new Uint8Array(wasm.derive_subkey(newPasswordKey, 'vault-auth'));
  const newKeyWrappingKey = new Uint8Array(wasm.derive_subkey(newPasswordKey, 'vault-enc'));

  try {
    const masterAad = _userId ? 'master:' + _userId : '';
    const encryptedMasterKey = encryptAndConcatV1(newKeyWrappingKey, masterKey, masterAad);
    const privkeyAad = _userId ? 'privkey:' + _userId : '';
    const encryptedPrivateKey = encryptAndConcatV1(encKey, privateKey, privkeyAad);

    return {
      current_auth_key: Array.from(currentAuthKey).map(b => b.toString(16).padStart(2, '0')).join(''),
      auth_key: Array.from(newAuthKey).map(b => b.toString(16).padStart(2, '0')).join(''),
      client_salt: Array.from(newSalt),
      encrypted_master_key: Array.from(encryptedMasterKey),
      public_key: Array.from(publicKey),
      encrypted_private_key: Array.from(encryptedPrivateKey),
    };
  } finally {
    wipe(currentPasswordKey, currentAuthKey, newPasswordKey, newKeyWrappingKey, newAuthKey);
  }
}

// --- HIBP breach check ---

export async function checkHIBP(password) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    const prefix = hashHex.slice(0, 5);
    const suffix = hashHex.slice(5);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, { signal: controller.signal });
    clearTimeout(timeout);

    if (!res.ok) return { breached: false, error: 'HIBP API error' };
    const text = await res.text();
    for (const line of text.split('\n')) {
      const [hash, count] = line.trim().split(':');
      if (hash === suffix) return { breached: true, count: parseInt(count, 10) };
    }
    return { breached: false };
  } catch (e) {
    return { breached: false, error: e.message || 'Network error' };
  }
}

// --- BIP39 mnemonic ---

export function generateBip39Mnemonic() {
  return wasm.generate_bip39_mnemonic();
}

export function validateBip39Mnemonic(mnemonic) {
  return wasm.validate_bip39_mnemonic(wasm.normalize_mnemonic(mnemonic));
}

export function deriveRecoveryKeys(mnemonic, version = 3) {
  const result = obj(wasm.derive_recovery_keys(mnemonic.normalize('NFKD'), version));
  return {
    wrappingKey: new Uint8Array(result.wrapping_key),
    authKey: new Uint8Array(result.auth_key),
  };
}

export function wrapMasterKeyForRecovery(wrappingKey) {
  if (!masterKey) throw new Error('Vault is locked');
  const aad = _userId ? 'recovery:' + _userId : '';
  return encryptAndConcatV1(new Uint8Array(wrappingKey), masterKey, aad);
}

export function wrapKeyForRecovery(wrappingKey, mkToWrap) {
  const aad = _userId ? 'recovery:' + _userId : '';
  return encryptAndConcatV1(new Uint8Array(wrappingKey), new Uint8Array(mkToWrap), aad);
}

// `recoveryFormatVersion` and `pkFormatVersion` are the per-row Phase 2
// columns from `/auth/recovery-params`:
// `users.recovery_wrapped_master_key_format_version` and
// `users.encrypted_private_key_format_version`.
export function unwrapMasterKeyFromRecovery(wrappingKey, wrappedBlob, encryptedPrivateKey, pubKeyArr, recoveryFormatVersion = undefined, pkFormatVersion = undefined) {
  syncUserIdFromToken();
  wipe(masterKey, encKey, wrapKey, privateKey, authKey);
  const wb = new Uint8Array(wrappedBlob);
  const wrappingKeyBytes = new Uint8Array(wrappingKey);
  // Mirror the master-key path's empty-AAD fallback in `deriveAndStoreKeys`:
  // `wrapMasterKeyForRecovery` takes the empty-AAD branch when `_userId`
  // is unset at write time. The read side here runs on an unauthenticated
  // request flow (the user has lost their password and isn't logged in),
  // so `_userId` may still be set from a stale module-level state from a
  // prior session, or unset. The forward direction we can recover from is
  // `_userId` set on read but write was empty-AAD: try the user-bound AAD
  // first, then fall back to empty. The reverse (write was user-bound,
  // read has no `_userId`) is unrecoverable without knowing the
  // write-time user_id, so it's intentionally not handled.
  let mk;
  if (_userId) {
    try {
      mk = decryptConcatAuto(wrappingKeyBytes, wb, 'recovery:' + _userId, recoveryFormatVersion);
    } catch (_) {
      mk = decryptConcatAuto(wrappingKeyBytes, wb, '', recoveryFormatVersion);
    }
  } else {
    mk = decryptConcatAuto(wrappingKeyBytes, wb, '', recoveryFormatVersion);
  }
  masterKey = mk;
  encKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-enc'));
  wrapKey = new Uint8Array(wasm.derive_subkey(mk, 'vault-wrap'));
  if (encryptedPrivateKey) {
    const epk = new Uint8Array(encryptedPrivateKey);
    try {
      privateKey = decryptConcatAuto(encKey, epk, _userId ? 'privkey:' + _userId : '', pkFormatVersion);
    } catch (_) {
      privateKey = decryptConcatAuto(encKey, epk, 'privkey', pkFormatVersion);
    }
  }
  if (pubKeyArr) publicKey = new Uint8Array(pubKeyArr);
}

// --- Drop wrapping (BIP39 mnemonic) ---

export function deriveDropLookupKey(mnemonic) {
  return wasm.derive_drop_lookup_key(mnemonic.normalize('NFKD'));
}

export function deriveDropWrappingKey(mnemonic, version = 1) {
  if (!validateBip39Mnemonic(mnemonic)) {
    throw new Error('Invalid BIP39 mnemonic');
  }
  return new Uint8Array(wasm.derive_drop_wrapping_key(mnemonic.normalize('NFKD'), version));
}

export function wrapDropKey(wrappingKey, dropKey, dropId) {
  const aad = dropId ? 'drop:' + dropId : '';
  return encryptAndConcatV1(wrappingKey, dropKey, aad);
}

export function unwrapDropKey(wrappingKey, wrapped, dropId) {
  const aad = dropId ? 'drop:' + dropId : '';
  return decryptConcatAuto(wrappingKey, wrapped, aad);
}

// --- Will wrapping (BIP39 mnemonic, same pattern as drops with different salt) ---

export function deriveWillLookupKey(mnemonic) {
  return wasm.derive_will_lookup_key(mnemonic.normalize('NFKD'));
}

export function deriveWillWrappingKey(mnemonic, version = 1) {
  if (!validateBip39Mnemonic(mnemonic)) {
    throw new Error('Invalid BIP39 mnemonic');
  }
  return new Uint8Array(wasm.derive_will_wrapping_key(mnemonic.normalize('NFKD'), version));
}

export function wrapWillKey(wrappingKey, willKey) {
  const aad = _userId ? 'will:' + _userId : '';
  return encryptAndConcatV1(wrappingKey, willKey, aad);
}

export function unwrapWillKey(wrappingKey, wrapped) {
  const aad = _userId ? 'will:' + _userId : '';
  return decryptConcatAuto(wrappingKey, wrapped, aad);
}

// --- Blob size padding ---
// Pads plaintext to size buckets to reduce size-correlation side channel.
// Format: 4-byte big-endian actual length prefix + original data + random padding.

export function padPlaintext(data) {
  return wasm.pad_plaintext(data);
}

export function unpadPlaintext(padded) {
  return wasm.unpad_plaintext(new Uint8Array(padded));
}

export function getMasterKeyBytes() {
  if (!masterKey) return null;
  return new Uint8Array(masterKey);
}

export function rewrapExistingMasterKey(newPassword) {
  if (!masterKey) throw new Error('Vault is locked');

  const clientSalt = new Uint8Array(16);
  crypto.getRandomValues(clientSalt);
  const passwordKey = new Uint8Array(wasm.derive_key(newPassword, clientSalt));
  const newAuthKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-auth'));
  const keyWrappingKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-enc'));

  try {
    const masterAad = _userId ? 'master:' + _userId : '';
    const encryptedMasterKey = encryptAndConcatV1(keyWrappingKey, masterKey, masterAad);
    const privkeyAad = _userId ? 'privkey:' + _userId : '';
    const encryptedPrivateKey = encryptAndConcatV1(encKey, privateKey, privkeyAad);

    return {
      client_salt: Array.from(clientSalt),
      public_key: Array.from(publicKey),
      encrypted_private_key: Array.from(encryptedPrivateKey),
      // Both wraps are minted V1 above (post-Phase-1, #122). Surface the
      // version on the return shape so callers feeding the result back
      // into `deriveAndStoreKeys` can pass it without guessing.
      encrypted_private_key_format_version: 1,
      auth_key: Array.from(newAuthKey).map(b => b.toString(16).padStart(2, '0')).join(''),
      encrypted_master_key: Array.from(encryptedMasterKey),
      encrypted_master_key_format_version: 1,
    };
  } finally {
    wipe(passwordKey, keyWrappingKey, newAuthKey);
  }
}

// --- Key rotation ---
// Generates a new master_key and re-wraps all item keys, private key, recovery, and passkeys.
// Re-encrypt a blob from V0 to V1 format using the same item key.
// Returns base64-encoded V1 blob, or null if already V1.
export function reEncryptBlobV1(itemWrappedKey, itemNonce, blobBase64, userId) {
  const itemKey = unwrapItemKey(itemWrappedKey, itemNonce);
  try {
    const blobBytes = Uint8Array.from(atob(blobBase64), c => c.charCodeAt(0));

    // Already V1 — skip
    if (blobBytes[0] === 0x01) return null;

    // V0: nonce(24) + ciphertext
    const nonce = blobBytes.slice(0, 24);
    const ciphertext = blobBytes.slice(24);
    const plaintext = new Uint8Array(wasm.decrypt(itemKey, ciphertext, nonce));

    // Re-encrypt as V1 with AAD
    const aad = userId ? 'item:' + userId : '';
    const combined = encryptAndConcatV1(itemKey, plaintext, aad);
    return uint8ToBase64(combined);
  } finally {
    wipe(itemKey);
  }
}

// Re-wrap file_wrapped_key inside an encrypted envelope from oldEncKey to newEncKey.
// Returns { blob: base64-encoded updated envelope, changed: true } or { changed: false }.
export function rewrapEnvelopeFileKeys(itemWrappedKey, itemNonce, blobBase64, userId, oldFileEncKey, newFileEncKey) {
  const itemKey = unwrapItemKey(itemWrappedKey, itemNonce);
  let fileKey;
  try {
    const blobBytes = Uint8Array.from(atob(blobBase64), c => c.charCodeAt(0));
    const aad = userId ? 'item:' + userId : '';
    const plainBytes = decryptConcatAuto(itemKey, blobBytes, aad);
    const plaintext = new TextDecoder().decode(unpadPlaintext(plainBytes));

    let envelope;
    try { envelope = JSON.parse(plaintext); } catch { return { changed: false }; }
    if (!envelope.file_wrapped_key || !envelope.file_nonce) return { changed: false };

    // Unwrap file key with old encKey
    const fileWK = new Uint8Array(envelope.file_wrapped_key);
    const fileNonce = new Uint8Array(envelope.file_nonce);
    const wrapAad = userId ? 'wrap:' + userId : '';
    const wrapAadBytes = new TextEncoder().encode(wrapAad);
    fileKey = new Uint8Array(wasm.decrypt_auto(oldFileEncKey, fileWK, fileNonce, wrapAadBytes));

    // Re-wrap with new encKey (V1; AAD bytes empty when no userId).
    const newPayload = obj(wasm.encrypt_v1(newFileEncKey, fileKey, wrapAadBytes));
    envelope.file_wrapped_key = Array.from(new Uint8Array(newPayload.ciphertext));
    envelope.file_nonce = Array.from(new Uint8Array(newPayload.nonce));

    // Re-encrypt envelope with same item key (V1)
    const updatedBytes = new TextEncoder().encode(JSON.stringify(envelope));
    const padded = padPlaintext(updatedBytes);
    const combined = encryptAndConcatV1(itemKey, padded, aad);
    return { changed: true, blob: uint8ToBase64(combined) };
  } finally {
    wipe(itemKey, fileKey);
  }
}

// Repair file keys using an old encKey (e.g. derived from recovery mnemonic).
// Same as rewrapEnvelopeFileKeys but uses current encKey as the new target.
export function repairFileKeys(itemWrappedKey, itemNonce, blobBase64, userId, oldEncKey) {
  return rewrapEnvelopeFileKeys(itemWrappedKey, itemNonce, blobBase64, userId, oldEncKey, encKey);
}

// Derive encKey from an old master key (recovered from recovery mnemonic) without touching globals.
export function deriveEncKeyFromMasterKey(mk) {
  return new Uint8Array(wasm.derive_subkey(new Uint8Array(mk), 'vault-enc'));
}

// Unwrap a master key from recovery_wrapped_master_key blob using recovery wrapping key, without setting globals.
export function unwrapMasterKeyRaw(wrappingKey, wrappedBlob, userId) {
  const wb = new Uint8Array(wrappedBlob);
  const recoveryAad = userId ? 'recovery:' + userId : '';
  return decryptConcatAuto(new Uint8Array(wrappingKey), wb, recoveryAad);
}

export function rotateMasterKey(items, password, saltArr, recoveryMnemonic, recoveryVersion, passkeyPrfOutputs) {
  const newMk = new Uint8Array(wasm.generate_random_key());
  const newEncKey = new Uint8Array(wasm.derive_subkey(newMk, 'vault-enc'));

  const salt = new Uint8Array(saltArr);
  const passwordKey = new Uint8Array(wasm.derive_key(password, salt));
  const keyWrappingKey = new Uint8Array(wasm.derive_subkey(passwordKey, 'vault-enc'));
  try {
    const masterAad = _userId ? 'master:' + _userId : '';
    const newEncryptedMasterKey = encryptAndConcatV1(keyWrappingKey, newMk, masterAad);

    const privkeyAad = _userId ? 'privkey:' + _userId : '';
    const newEncryptedPrivateKey = encryptAndConcatV1(newEncKey, privateKey, privkeyAad);

    // Re-wrap all item keys with V1 (AAD bytes empty when no _userId).
    const wrapAad = _userId ? 'wrap:' + _userId : '';
    const wrapAadBytes = new TextEncoder().encode(wrapAad);
    const migratedItems = items.map(item => {
      const itemKey = unwrapItemKey(item.wrapped_key, item.nonce);
      try {
        const payload = obj(wasm.encrypt_v1(newEncKey, itemKey, wrapAadBytes));
        return {
          item_id: item.id,
          wrapped_key: Array.from(new Uint8Array(payload.ciphertext)),
          nonce: Array.from(new Uint8Array(payload.nonce)),
        };
      } finally {
        wipe(itemKey);
      }
    });

    const result = {
      encrypted_master_key: Array.from(newEncryptedMasterKey),
      encrypted_private_key: Array.from(newEncryptedPrivateKey),
      items: migratedItems,
      recovery_wrapped_master_key: null,
      passkey_rewraps: [],
      newMk,
      newEncKey,
    };

    if (recoveryMnemonic) {
      result._recoveryMnemonic = recoveryMnemonic;
      result._recoveryVersion = recoveryVersion;
    }

    // Re-wrap passkey PRF wrappings with V1
    if (passkeyPrfOutputs && passkeyPrfOutputs.length) {
      const passkeyAad = _userId ? 'passkey:' + _userId : '';
      result.passkey_rewraps = passkeyPrfOutputs.map(pk => {
        const passkeyEncKey = new Uint8Array(wasm.derive_subkey(new Uint8Array(pk.prf_output), 'vault-passkey-v1'));
        try {
          const wrapped = encryptAndConcatV1(passkeyEncKey, newMk, passkeyAad);
          return {
            credential_id: pk.credential_id,
            prf_wrapped_master_key: Array.from(wrapped),
          };
        } finally {
          wipe(passkeyEncKey);
        }
      });
    }

    return result;
  } finally {
    wipe(passwordKey, keyWrappingKey);
  }
}

// Apply rotated keys to in-memory state after server confirms
export function applyRotatedKeys(newMk, newEncKey) {
  wipe(masterKey, encKey, wrapKey);
  masterKey = new Uint8Array(newMk);
  encKey = new Uint8Array(newEncKey);
  wrapKey = new Uint8Array(wasm.derive_subkey(masterKey, 'vault-wrap'));
}

export function clearKeys() {
  if (masterKey) masterKey.fill(0);
  if (encKey) encKey.fill(0);
  if (wrapKey) wrapKey.fill(0);
  if (privateKey) privateKey.fill(0);
  if (authKey) authKey.fill(0);
  masterKey = encKey = wrapKey = privateKey = publicKey = authKey = null;
}

// --- Inactivity timeout: clear keys after 15 min idle ---
let _inactivityTimer = null;
// --- Consolidated link-secret grant helpers (WASM-backed) ---

/**
 * Prepare a link-secret grant from a raw item key and optional file key.
 * Returns { wrapped_key, nonce, link_secret, claim_key, claim_ciphertext,
 *           claim_token_hash, file_wrapped_key }.
 * All values are Uint8Arrays except claim_token_hash (string).
 */
export function prepareLinkGrant(itemKey, fileKey) {
  const ik = new Uint8Array(itemKey);
  const fk = fileKey ? new Uint8Array(fileKey) : new Uint8Array(0);
  const result = obj(wasm.prepare_link_grant(ik, fk));
  return {
    wrapped_key: new Uint8Array(result.wrapped_key),
    nonce: new Uint8Array(result.nonce),
    link_secret: new Uint8Array(result.link_secret),
    claim_key: new Uint8Array(result.claim_key),
    claim_ciphertext: new Uint8Array(result.claim_ciphertext),
    claim_token_hash: result.claim_token_hash,
    file_wrapped_key: result.file_wrapped_key?.length ? new Uint8Array(result.file_wrapped_key) : null,
  };
}

/**
 * Decrypt a link-secret grant given the claim_key from the share URL.
 * Returns the decrypted SecretBlob object.
 */
export function decryptLinkGrant(claimKey, claimCiphertext, wrappedKey, nonce, blobData, grantorId) {
  return obj(wasm.decrypt_link_grant(
    new Uint8Array(claimKey),
    new Uint8Array(claimCiphertext),
    new Uint8Array(wrappedKey),
    new Uint8Array(nonce),
    new Uint8Array(blobData),
    grantorId || '',
  ));
}

/**
 * Unwrap a link-secret grant's item key without decrypting the blob.
 */
export function unwrapLinkGrantKey(claimKey, claimCiphertext, wrappedKey, nonce) {
  return new Uint8Array(wasm.unwrap_link_grant_key(
    new Uint8Array(claimKey),
    new Uint8Array(claimCiphertext),
    new Uint8Array(wrappedKey),
    new Uint8Array(nonce),
  ));
}

/**
 * Parse a grant-accept URL into { grant_id, claim_key_b64 }.
 */
export function parseGrantUrl(url) {
  return obj(wasm.parse_grant_url(url));
}

// --- Claim token crypto (SubtleCrypto, no WASM) ---

export async function encryptClaimSecret(claimKey, linkSecret) {
  const key = await crypto.subtle.importKey('raw', claimKey, 'AES-GCM', false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, linkSecret);
  const result = new Uint8Array(12 + ct.byteLength);
  result.set(iv);
  result.set(new Uint8Array(ct), 12);
  return result;
}

export async function decryptClaimSecret(claimKey, claimCiphertext) {
  const data = new Uint8Array(claimCiphertext);
  const iv = data.slice(0, 12);
  const ct = data.slice(12);
  const key = await crypto.subtle.importKey('raw', claimKey, 'AES-GCM', false, ['decrypt']);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return new Uint8Array(pt);
}

export async function sha256hex(data) {
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// --- API Key helpers ---

export async function createApiKey(name, scoped, selectedItemIds, readOnly, expiresAt) {
  if (!masterKey) throw new Error('Keys not derived yet');

  // Generate 32-byte random secret
  const secret = new Uint8Array(wasm.generate_random_key());
  const keys = obj(wasm.derive_api_key_keys(secret));
  const wrappingKey = new Uint8Array(keys.wrapping_key);
  const apiAuthKey = new Uint8Array(keys.auth_key);

  const prefix = 'vk_' + Array.from(secret.slice(0, 4)).map(b => b.toString(16).padStart(2, '0')).join('');

  let body = {
    name,
    auth_key: Array.from(apiAuthKey).map(b => b.toString(16).padStart(2, '0')).join(''),
    key_prefix: prefix,
    scopes: { read_only: readOnly || false },
  };
  wipe(apiAuthKey);

  if (expiresAt) body.expires_at = expiresAt;

  try {
    if (scoped) {
      // Generate X25519 keypair for the API key
      const kp = obj(wasm.generate_keypair());
      const privKey = new Uint8Array(kp.private_key);
      const pubKey = new Uint8Array(kp.public_key);

      // Wrap private key with API wrapping key
      const wrappedPrivKey = new Uint8Array(wasm.wrap_key_symmetric(wrappingKey, privKey));
      wipe(privKey);
      body.encrypted_private_key = Array.from(wrappedPrivKey);
      body.public_key = Array.from(pubKey);

      // Create the key
      const { post } = await import('./api.js');
      const result = await post('/api-keys', body);

      // Grant selected items
      if (selectedItemIds && selectedItemIds.length > 0) {
        const localEncKey = new Uint8Array(wasm.derive_subkey(masterKey, 'vault-enc'));
        try {
          for (const itemId of selectedItemIds) {
            await grantItemToApiKey(result.id, pubKey, itemId, localEncKey);
          }
        } finally {
          wipe(localEncKey);
        }
      }

      const displayKey = prefix + '_' + base64urlEncode(secret);
      return { result, displayKey };
    } else {
      // Full-access key: wrap user's master key
      const wrappedMasterKey = new Uint8Array(wasm.wrap_key_symmetric(wrappingKey, masterKey));
      body.wrapped_master_key = Array.from(wrappedMasterKey);
      body.encrypted_private_key = Array.from(encryptedPrivateKey);

      const { post } = await import('./api.js');
      const result = await post('/api-keys', body);
      const displayKey = prefix + '_' + base64urlEncode(secret);
      return { result, displayKey };
    }
  } finally {
    wipe(secret, wrappingKey);
  }
}

export async function grantItemToApiKey(apiKeyId, apiPubKey, itemId, encKey) {
  let derivedEncKey = null;
  if (!encKey) {
    derivedEncKey = new Uint8Array(wasm.derive_subkey(masterKey, 'vault-enc'));
    encKey = derivedEncKey;
  }

  try {
    // Fetch item to get wrapped_key and nonce
    const { get, post } = await import('./api.js');
    const item = await get(`/items/${itemId}`);

    const wrappedKey = new Uint8Array(item.wrapped_key);
    const nonce = new Uint8Array(item.nonce);

    // Unwrap item key using user's enc_key
    const itemKey = new Uint8Array(wasm.decrypt(encKey, wrappedKey, nonce));

    // Wrap item key for API key's public key (V1: key-bound HKDF + AAD).
    // Issue #178 closed the last V0 emitter on this path; the server still
    // accepts format_version=0 so older builds in the wild stay readable.
    const grant = obj(wasm.wrap_key_for_recipient_v1(itemKey, apiPubKey));
    wipe(itemKey);

    await post(`/api-keys/${apiKeyId}/grants`, {
      item_id: itemId,
      wrapped_key: Array.from(new Uint8Array(grant.wrapped_key)),
      ephemeral_pubkey: Array.from(new Uint8Array(grant.ephemeral_pubkey)),
      nonce: Array.from(new Uint8Array(grant.nonce)),
      format_version: 1,
    });
  } finally {
    wipe(derivedEncKey);
  }
}

export async function revokeApiKeyGrant(apiKeyId, grantId) {
  const { del } = await import('./api.js');
  await del(`/api-keys/${apiKeyId}/grants/${grantId}`);
}

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

function resetInactivityTimer() {
  if (!masterKey) return; // no keys loaded, nothing to protect
  if (_inactivityTimer) clearTimeout(_inactivityTimer);
  _inactivityTimer = setTimeout(() => {
    if (masterKey) {
      clearKeys();
      _removeStoredToken();
      _setSessionExpired();
      window.location.hash = '#/login';
    }
  }, INACTIVITY_TIMEOUT_MS);
}

// Start monitoring on user interaction
['click', 'keydown', 'mousemove', 'touchstart', 'scroll'].forEach(evt => {
  document.addEventListener(evt, resetInactivityTimer, { passive: true });
});
